import { Box, Button, Stack, Typography } from "@mui/material"
import { FC } from "react"
import {
  DRIFT_CLASS_KEYS,
  NON_DRIFT_KEYS,
  NON_DRIFT_LABEL,
  type NonDriftKey,
} from "./driftClasses"
import { fmtCount } from "./ReservationSyncChrome"

/**
 * The class filters, in TWO visually distinct groups (LOCKER-421 `DoD8`).
 *
 * Today's console renders one flat row of chips, so a defect class and a
 * divergence carry the same weight and the operator has to already know the
 * difference to read it.
 *
 * **Solid border = a sync may act on it. Dashed border = no sync ever writes it.**
 * The dashed border is the whole affordance: it says "nothing here is a write"
 * before anyone reads the label. That is a property of the border, not of the
 * colour, which is why it survives a greyscale screenshot and a colour-blind
 * reader.
 */
export type ClassFilter = { group: "all" } | { group: "drift" | "nonDrift"; key: string }

const filterKey = (filter: ClassFilter) =>
  filter.group === "all" ? "all" : `${filter.group}:${filter.key}`

const FilterButton: FC<{
  label: string
  /** `null` when the source behind this class was never read — an em dash, not 0. */
  count: number | null
  selected: boolean
  dashed?: boolean
  loud?: boolean
  onClick: () => void
}> = ({ label, count, selected, dashed, loud, onClick }) => (
  <Button
    size="small"
    onClick={onClick}
    variant={selected ? "contained" : "outlined"}
    color={loud && (count ?? 0) > 0 ? "warning" : "inherit"}
    sx={{
      textTransform: "none",
      fontSize: "0.72rem",
      fontWeight: (count ?? 0) > 0 ? 600 : 400,
      minHeight: 32,
      borderStyle: dashed ? "dashed" : "solid",
      color: !selected && !count ? "text.disabled" : undefined,
    }}
  >
    {label} ({fmtCount(count)})
  </Button>
)

export const DriftClassFilters: FC<{
  /** Per drift class, from the run's stored counts. */
  counts: Record<string, number> | null
  /** Per counted-never-drift class. A `null` VALUE means that source was not read. */
  nonDriftCounts: Record<string, number | null> | null | undefined
  driftTotal: number | null
  selected: ClassFilter
  onSelect: (filter: ClassFilter) => void
}> = ({ counts, nonDriftCounts, driftTotal, selected, onSelect }) => (
  <Stack
    direction="row"
    spacing={3}
    alignItems="flex-start"
    flexWrap="wrap"
    useFlexGap
    sx={{ mb: 2 }}
  >
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, letterSpacing: "0.07em", color: "text.disabled" }}
      >
        DRIFT — A SYNC MAY ACT ON THESE
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        <FilterButton
          label="all"
          count={driftTotal ?? 0}
          selected={selected.group === "all"}
          onClick={() => onSelect({ group: "all" })}
        />
        {DRIFT_CLASS_KEYS.map((key) => (
          <FilterButton
            key={key}
            label={key}
            count={counts?.[key] ?? 0}
            selected={selected.group === "drift" && selected.key === key}
            onClick={() => onSelect({ group: "drift", key })}
          />
        ))}
      </Stack>
    </Box>

    <Box sx={{ borderLeft: { md: 1 }, borderColor: "divider", pl: { md: 3 } }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, letterSpacing: "0.07em", color: "text.disabled" }}
      >
        COUNTED, NEVER DRIFT — NO SYNC EVER WRITES THESE
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        {NON_DRIFT_KEYS.map((key: NonDriftKey) => (
          <FilterButton
            key={key}
            dashed
            loud={key === "poisonedExpiry" || key === "knownProjectionDefect"}
            label={NON_DRIFT_LABEL[key]}
            count={nonDriftCounts?.[key] ?? null}
            selected={selected.group === "nonDrift" && selected.key === key}
            onClick={() => onSelect({ group: "nonDrift", key })}
          />
        ))}
      </Stack>
    </Box>
  </Stack>
)

export const isSelected = (filter: ClassFilter, key: string) =>
  filterKey(filter) === key
